/**
 * BuddyHub — Iter 92 "Buddy Program"
 *
 * Landing page for anyone assigned as a buddy to one or more new hires.
 *   • Cards for each hire (progress + last coffee + vibe pulse)
 *   • Log a coffee (schedule / complete)
 *   • Log a private vibe note (visible to HR only; low scores auto-alert HR)
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  Coffee, HeartHandshake, Smile, Meh, Frown, Sparkles,
  Calendar as CalendarIcon, ClipboardList, Lock, Flame, Trophy,
} from "lucide-react";

// Vibe scale: 1 (angry) → 5 (delighted)
const VIBE_FACES = [
  { v: 1, label: "Struggling", cls: "text-red-500 border-red-500/30 bg-red-500/5" },
  { v: 2, label: "Rough", cls: "text-orange-500 border-orange-500/30 bg-orange-500/5" },
  { v: 3, label: "Neutral", cls: "text-amber-500 border-amber-500/30 bg-amber-500/5" },
  { v: 4, label: "Good", cls: "text-emerald-500 border-emerald-500/30 bg-emerald-500/5" },
  { v: 5, label: "Great", cls: "text-primary border-primary/30 bg-primary/5" },
];

function VibeEmoji({ score, size = "sm" }) {
  const s = Number(score) || 0;
  const Icon = s <= 2 ? Frown : s <= 3 ? Meh : Smile;
  const cls = s <= 2 ? "text-red-500" : s <= 3 ? "text-amber-500" : "text-emerald-500";
  return <Icon className={`${size === "lg" ? "w-6 h-6" : "w-4 h-4"} ${cls}`} />;
}

function fmtRelDay(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 0) return `in ${Math.abs(days)}d`;
  return `${days}d ago`;
}

export default function BuddyHub() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [hires, setHires] = useState(null);   // null = loading, [] = empty
  const [selected, setSelected] = useState(null);   // selected hire card
  const [coffeeOpen, setCoffeeOpen] = useState(false);
  const [vibeOpen, setVibeOpen] = useState(false);
  const [streak, setStreak] = useState(null);
  const [board, setBoard] = useState([]);
  const [champions, setChampions] = useState([]);

  const load = async () => {
    if (!wsId) return;
    try {
      const [hRes, sRes, lRes, cRes] = await Promise.all([
        api.get(`/me/buddy/hires?ws_id=${wsId}`),
        api.get(`/me/buddy/streak?ws_id=${wsId}`).catch(() => ({ data: null })),
        api.get(`/workspaces/${wsId}/buddy/leaderboard?limit=10`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${wsId}/buddy/champion/latest`).catch(() => ({ data: [] })),
      ]);
      setHires(hRes.data);
      setStreak(sRes.data);
      setBoard(lRes.data || []);
      setChampions(cRes.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load buddy dashboard"));
      setHires([]);
    }
  };
  useEffect(() => { load(); }, [wsId]);

  return (
    <div className="space-y-6" data-testid="buddy-hub-page">
      <PageHeader
        title="Buddy Program"
        subtitle="Your assigned new hires — one coffee makes a huge difference."
        icon={HeartHandshake}
      />

      {/* Streak + leaderboard row */}
      {(streak || board.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {streak && (
            <div className="border border-border p-4 bg-gradient-to-br from-orange-500/5 to-transparent" data-testid="buddy-streak-card">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                <Flame className={`w-3 h-3 ${streak.active_this_week ? "text-orange-500" : "text-muted-foreground"}`} /> Your streak
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-4xl font-bold tabular-nums" data-testid="buddy-streak-weeks">{streak.current_streak_weeks}</span>
                <span className="text-sm text-muted-foreground">week{streak.current_streak_weeks === 1 ? "" : "s"}</span>
                {streak.active_this_week && <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-orange-500/10 text-orange-600 border border-orange-500/30 ml-auto">🔥 active</span>}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Longest streak: <b>{streak.longest_streak_weeks}</b> week{streak.longest_streak_weeks === 1 ? "" : "s"} · Log a coffee or vibe note before Sunday UTC to extend.
              </div>
            </div>
          )}
          {board.length > 0 && (
            <div className="md:col-span-2 border border-border" data-testid="buddy-leaderboard">
              <div className="px-4 py-2 border-b border-border flex items-center gap-2">
                <Trophy className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-[10px] uppercase tracking-widest font-semibold">Buddy leaderboard · top {board.length}</span>
              </div>
              <div className="divide-y divide-border">
                {board.map((row, i) => (
                  <div key={row.buddy?.id} className="px-4 py-2 grid grid-cols-12 gap-2 items-center text-sm" data-testid={`buddy-lb-row-${i}`}>
                    <div className="col-span-1 text-center">
                      {i === 0 ? <Trophy className="w-4 h-4 text-amber-500 mx-auto" /> : <span className="text-xs font-mono text-muted-foreground">#{i + 1}</span>}
                    </div>
                    <div className="col-span-5 flex items-center gap-2 min-w-0">
                      {row.buddy.avatar_data_url ? (
                        <img src={row.buddy.avatar_data_url} alt="" className="w-6 h-6 object-cover" />
                      ) : (
                        <div className="w-6 h-6 bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-bold">
                          {(row.buddy.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <span className="font-medium truncate">{row.buddy.name || "—"}</span>
                    </div>
                    <div className="col-span-2 text-xs flex items-center gap-1">
                      <Flame className={`w-3 h-3 ${row.active_this_week ? "text-orange-500" : "text-muted-foreground"}`} />
                      <span className="font-mono">{row.current_streak_weeks}w</span>
                    </div>
                    <div className="col-span-2 text-xs text-muted-foreground text-right"><span className="font-mono">{row.touchpoints_28d}</span> touch</div>
                    <div className="col-span-2 text-xs text-muted-foreground text-right"><span className="font-mono">{row.assigned_hires}</span> hire{row.assigned_hires === 1 ? "" : "s"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Buddy Champion Wall of Fame — last 12 weeks */}
      {champions.length > 0 && (
        <div className="border border-border" data-testid="buddy-champion-wall">
          <div className="px-4 py-2 border-b border-border flex items-center gap-2">
            <Trophy className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-[10px] uppercase tracking-widest font-semibold">Buddy Champions · last {champions.length} week{champions.length === 1 ? "" : "s"}</span>
          </div>
          <div className="p-4 flex gap-3 overflow-x-auto">
            {champions.map((c) => (
              <div key={c.id} className="flex-shrink-0 w-40 border border-border p-3 bg-gradient-to-br from-amber-500/5 to-transparent" data-testid={`champion-${c.week_key}`}>
                <div className="flex items-center gap-2 mb-2">
                  {c.user.avatar_data_url ? (
                    <img src={c.user.avatar_data_url} alt="" className="w-8 h-8 object-cover" />
                  ) : (
                    <div className="w-8 h-8 bg-amber-500 text-white flex items-center justify-center text-sm font-bold">
                      {(c.user.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <Trophy className="w-3.5 h-3.5 text-amber-500 ml-auto" />
                </div>
                <div className="text-sm font-semibold truncate">{c.user.name || "—"}</div>
                <div className="text-[10px] text-muted-foreground font-mono">{c.week_key}</div>
                <div className="text-[10px] font-semibold text-amber-600 mt-1">+{c.points} pts</div>
              </div>
            ))}
          </div>
        </div>
      )}


      {hires === null && (
        <Card className="rounded-none"><CardContent className="p-8 text-sm text-muted-foreground">Loading…</CardContent></Card>
      )}
      {hires !== null && hires.length === 0 && (
        <Card className="rounded-none" data-testid="buddy-empty">
          <CardContent className="p-8 text-center">
            <HeartHandshake className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
            <div className="text-base font-semibold">No hires assigned yet</div>
            <div className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              When HR pairs you with a new joiner, they&apos;ll show up here. You&apos;ll get gentle nudges to catch up over a 15-min coffee.
            </div>
          </CardContent>
        </Card>
      )}
      {hires && hires.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {hires.map((h) => (
            <HireCard
              key={h.employee_id}
              hire={h}
              onCoffee={() => { setSelected(h); setCoffeeOpen(true); }}
              onVibe={() => { setSelected(h); setVibeOpen(true); }}
            />
          ))}
        </div>
      )}

      {coffeeOpen && (
        <CoffeeDialog
          wsId={wsId}
          hire={selected}
          onClose={() => setCoffeeOpen(false)}
          onSaved={() => { setCoffeeOpen(false); load(); }}
        />
      )}
      {vibeOpen && (
        <VibeNoteDialog
          wsId={wsId}
          hire={selected}
          onClose={() => setVibeOpen(false)}
          onSaved={() => { setVibeOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function HireCard({ hire, onCoffee, onVibe }) {
  const days = hire.days_since_joining;
  const dayBadge = days == null ? null :
    days < 0 ? `starts in ${Math.abs(days)}d` :
    days === 0 ? "joined today" :
    `day ${days + 1}`;
  const stale = hire.coffee_stale_days == null || hire.coffee_stale_days >= 14;
  const atRisk = hire.last_vibe_score != null && hire.last_vibe_score <= 2;
  const initials = (hire.employee_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <Card className="rounded-none group hover:border-primary transition-colors" data-testid={`buddy-hire-card-${hire.employee_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 shrink-0 bg-primary text-primary-foreground flex items-center justify-center font-bold rounded-none">
            {hire.avatar_data_url ? (
              <img src={hire.avatar_data_url} alt="" className="w-full h-full object-cover" />
            ) : initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-semibold truncate" data-testid={`buddy-hire-name-${hire.employee_id}`}>
              {hire.employee_name || "—"}
            </div>
            <div className="text-xs text-muted-foreground truncate">{hire.designation || "—"}</div>
            {dayBadge && (
              <span className="mt-1 inline-block text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5">
                {dayBadge}
              </span>
            )}
          </div>
          {atRisk && (
            <span className="text-[10px] font-semibold px-2 py-0.5 bg-red-500/10 text-red-600 border border-red-500/30" data-testid={`buddy-hire-atrisk-${hire.employee_id}`}>
              at-risk
            </span>
          )}
        </div>

        {/* Onboarding progress */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
            <span>Onboarding progress</span>
            <span className="font-mono">{hire.onboarding_progress_pct}%</span>
          </div>
          <div className="h-1.5 bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${hire.onboarding_progress_pct}%` }} />
          </div>
        </div>

        {/* Signals */}
        <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
          <div className={`border ${stale ? "border-amber-500/40 bg-amber-500/5" : "border-border"} px-2 py-1.5`}>
            <div className="flex items-center gap-1 text-muted-foreground uppercase tracking-wider text-[9px]">
              <Coffee className="w-3 h-3" /> Last coffee
            </div>
            <div className="font-semibold mt-0.5">
              {hire.last_coffee_at ? fmtRelDay(hire.last_coffee_at) : "—"}
            </div>
          </div>
          <div className={`border ${atRisk ? "border-red-500/40 bg-red-500/5" : "border-border"} px-2 py-1.5`}>
            <div className="flex items-center gap-1 text-muted-foreground uppercase tracking-wider text-[9px]">
              <Sparkles className="w-3 h-3" /> Vibe
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              {hire.last_vibe_score ? (
                <>
                  <VibeEmoji score={hire.last_vibe_score} />
                  <span className="font-mono font-semibold">{hire.last_vibe_score}/5</span>
                </>
              ) : <span className="text-muted-foreground">—</span>}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 flex gap-2">
          <Button size="sm" variant="outline" className="rounded-none flex-1"
                  onClick={onCoffee} data-testid={`buddy-log-coffee-${hire.employee_id}`}>
            <Coffee className="w-3.5 h-3.5 mr-1.5" /> Log coffee
          </Button>
          <Button size="sm" className="rounded-none flex-1"
                  onClick={onVibe} data-testid={`buddy-log-vibe-${hire.employee_id}`}>
            <ClipboardList className="w-3.5 h-3.5 mr-1.5" /> Vibe note
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CoffeeDialog({ wsId, hire, onClose, onSaved }) {
  const [when, setWhen] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(15, 0, 0, 0);
    // Format for datetime-local: "YYYY-MM-DDTHH:MM" in LOCAL time (not UTC)
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!when) { toast.error("Pick a date & time"); return; }
    setBusy(true);
    try {
      await api.post(`/me/buddy/hires/${hire.employee_id}/coffee`, {
        ws_id: wsId,
        scheduled_at: new Date(when).toISOString(),
        note: note || null,
      });
      toast.success(`Coffee logged with ${hire.employee_name?.split(" ")[0] || "your hire"} ☕`);
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to log coffee")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="buddy-coffee-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Coffee className="w-4 h-4" /> Coffee with {hire.employee_name}</DialogTitle>
          <DialogDescription>
            Even a 15-min catch-up helps. Pick a slot — the hire won&apos;t see this log, but HR can spot pairs that need a nudge.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1.5">When</div>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
                   className="h-10" data-testid="buddy-coffee-when" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1.5">Note (optional)</div>
            <Textarea rows={2} placeholder="Topics you want to cover…"
                      value={note} onChange={(e) => setNote(e.target.value)} data-testid="buddy-coffee-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="buddy-coffee-save">
            {busy ? "Saving…" : "Log coffee"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VibeNoteDialog({ wsId, hire, onClose, onSaved }) {
  const [vibe, setVibe] = useState(3);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/me/buddy/hires/${hire.employee_id}/vibe-note`, {
        ws_id: wsId, vibe, note: note || null,
      });
      if (data.hr_flagged) {
        toast.success("Vibe logged — HR has been notified so they can support.");
      } else {
        toast.success("Vibe logged privately for HR.");
      }
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to log vibe")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="buddy-vibe-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Vibe check · {hire.employee_name}</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Visible only to HR. Low scores automatically flag for support.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
              How&apos;s <b>{(hire.employee_name || "they").split(" ")[0]}</b> feeling?
            </div>
            <div className="grid grid-cols-5 gap-2">
              {VIBE_FACES.map((f) => (
                <button key={f.v}
                        onClick={() => setVibe(f.v)}
                        className={`flex flex-col items-center gap-1 py-2 border-2 transition ${vibe === f.v ? f.cls + " border-current" : "border-border hover:border-primary/30"}`}
                        data-testid={`buddy-vibe-${f.v}`}>
                  <VibeEmoji score={f.v} size="lg" />
                  <span className="text-[9px] uppercase tracking-wider font-semibold">{f.v}</span>
                </button>
              ))}
            </div>
            <div className="text-center text-xs text-muted-foreground mt-2">{VIBE_FACES.find((x) => x.v === vibe)?.label}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1.5">Anything HR should know?</div>
            <Textarea rows={3} placeholder="e.g. Struggling with tooling, seems isolated in meetings…"
                      value={note} onChange={(e) => setNote(e.target.value)} data-testid="buddy-vibe-note-text" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="buddy-vibe-save">
            {busy ? "Saving…" : "Save private note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
